import { api, toast, useAuthStore } from '@bemor/web-kit';
import { Button, ReasonDialog } from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { ApprovalPending } from '@/api/types';
import { useWrite } from '@/lib/queries';

/**
 * BM-93 decision 8: one lifecycle action on an organization, a store or a user. Every action takes a reason in the
 * kit's ReasonDialog with its consequence line (design 0.9.0 section 6); 200 refreshes the page, 202 means the
 * tenant policy sent it to Approvals (a toast with the link). "Sign in as" answers a one-time link that opens the
 * dashboard in a new tab.
 */
export interface LifecycleAction {
    key: string;
    /** `POST` path, e.g. `/staff/stores/sto_1/suspend`. */
    path: string;
    label: string;
    title: string;
    /** The consequence line under the reason. */
    description: string;
    permission: string;
    destructive?: boolean;
    /** Whether the subject's state allows it now. */
    enabled: boolean;
    /** The action answers `{url}` to open in a new tab (impersonation). */
    opens?: boolean;
}

export function isPending(result: unknown): result is ApprovalPending {
    return (
        typeof result === 'object' &&
        result !== null &&
        (result as { pending?: unknown }).pending === true
    );
}

export function LifecycleActions({
    actions,
    invalidate,
    testId = 'lifecycle-actions',
}: {
    actions: LifecycleAction[];
    /** The query prefixes to refresh after a 200. */
    invalidate: string[];
    testId?: string;
}) {
    const can = useAuthStore((s) => s.can);
    const [current, setCurrent] = useState<LifecycleAction | null>(null);
    const act = useWrite(
        async (body: { action: LifecycleAction; reason: string }) => {
            const response = await api().post<
                ApprovalPending | { url?: string }
            >(body.action.path, { reason: body.reason });
            return { action: body.action, result: response.data };
        },
        invalidate,
    );
    const visible = actions.filter((a) => can(a.permission));
    if (visible.length === 0) return null;
    return (
        <div className="flex flex-wrap gap-2" data-testid={testId}>
            {visible.map((a) => (
                <Button
                    key={a.key}
                    size="sm"
                    variant={a.destructive ? 'destructive' : 'outline'}
                    disabled={!a.enabled}
                    onClick={() => setCurrent(a)}
                    data-testid={`action-${a.key}`}
                >
                    {a.label}
                </Button>
            ))}
            {current ? (
                <ReasonDialog
                    open
                    onOpenChange={(o) => (o ? null : setCurrent(null))}
                    title={current.title}
                    description={current.description}
                    confirmLabel={current.label}
                    destructive={current.destructive}
                    required
                    busy={act.isPending}
                    error={act.error}
                    onConfirm={(reason) =>
                        act.mutate(
                            { action: current, reason },
                            {
                                onSuccess: ({ action, result }) => {
                                    setCurrent(null);
                                    if (isPending(result)) {
                                        toast('Sent for approval', {
                                            description: (
                                                <span>
                                                    A second staff member
                                                    decides it in{' '}
                                                    {/* a plain link: the toast renders outside the router */}
                                                    <a
                                                        href="/approvals"
                                                        className="underline"
                                                    >
                                                        Approvals
                                                    </a>
                                                    .
                                                </span>
                                            ),
                                        });
                                        return;
                                    }
                                    if (
                                        action.opens &&
                                        typeof (result as { url?: string })
                                            .url === 'string'
                                    ) {
                                        window.open(
                                            (result as { url: string }).url,
                                            '_blank',
                                            'noopener',
                                        );
                                        return;
                                    }
                                    toast(`${action.label}: done`);
                                },
                            },
                        )
                    }
                />
            ) : null}
        </div>
    );
}
